import { fetchApi } from '@/services/api-client';

import type { AllergySeverity, EnrolmentStatus } from '../students/students';

export const STUDENT_DIRECTORY_PATH = '/student-directory';

export type DirectoryAllergy = { label: string; severe: boolean };

// All-authenticated endpoints (admin/teacher/employee) — the server returns a safe
// subset only. Parents carry name + relationship, never contact details.
export type DirectoryListItem = {
  id: string;
  displayName: string;
  fullName: string;
  email: string | null;
  photoUrl: string;
  gradeLevel: number | null;
  houseName: string | null;
  dateOfBirth: string | null;
  sandDollarTotal: number;
  allergies: DirectoryAllergy[];
  dietaryNote: string | null;
  allergySeverity: AllergySeverity;
  hasAllergyInfo: boolean;
  canShareToSocial: boolean;
  enrolmentStatus: EnrolmentStatus;
  trialEndDate: string | null;
};

/** A child of the active year not in class yet: admitted, or a trial that starts later. */
export type DirectoryArrivingItem = {
  id: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  gradeLevel: number;
  enrolmentStatus: 'admitted' | 'trial';
  /** The first day: the start date, or for a trial its start. */
  startDate: string | null;
  trialEndDate: string | null;
  allergies: DirectoryAllergy[];
  dietaryNote: string | null;
  allergySeverity: AllergySeverity;
  hasAllergyInfo: boolean;
};

export type DirectoryParent = { name: string; relationship: string | null };

export type DirectoryDetail = {
  id: string;
  displayName: string;
  fullName: string;
  nickname: string | null;
  dateOfBirth: string | null;
  gradeLevel: number | null;
  houseName: string | null;
  photoUrl: string;
  sandDollarTotal: number;
  allergies: DirectoryAllergy[];
  dietaryNote: string | null;
  allergySeverity: AllergySeverity;
  canShareToSocial: boolean;
  socialNote: string | null;
  parents: DirectoryParent[];
};

export type StudentDirectoryFilters = {
  search?: string;
  grade?: number;
};

function directoryQuery(filters: StudentDirectoryFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.grade !== undefined) params.append('grade', String(filters.grade));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const studentDirectoryApi = {
  list: (filters: StudentDirectoryFilters = {}) =>
    fetchApi<DirectoryListItem[]>(`${STUDENT_DIRECTORY_PATH}${directoryQuery(filters)}`),
  arriving: (filters: StudentDirectoryFilters = {}) =>
    fetchApi<DirectoryArrivingItem[]>(`${STUDENT_DIRECTORY_PATH}/arriving${directoryQuery(filters)}`),
  detail: (id: string) => fetchApi<DirectoryDetail>(`${STUDENT_DIRECTORY_PATH}/${id}`),
};
